import React from 'react';
import PropTypes from 'prop-types';

/**
 * MessageSearchResults — Tri-section search results view for the /message route.
 *
 * Replaces the ChatPanel when a search query is active.
 * Sections: Messages, Requests, Suggestions.
 */

/* ── Platform Icons ───────────────────────────────────────────────────── */
function TwitchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="msg-search__platform-icon msg-search__platform-icon--twitch">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="msg-search__platform-icon msg-search__platform-icon--youtube">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

function highlightMatch(text, query) {
  if (!query) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i} className="msg-search__highlight">{part}</mark> : part
  );
}

function MessageSearchResults({ query, conversations, userSuggestions, onSelectConversation, onStartConversation, onClearSearch }) {
  const q = query.toLowerCase();

  // Filter conversations by sender name or message content
  const matchMessages = conversations
    .filter((c) => (c.tabType || (c.isPaidRequest ? 'requests' : 'messages')) === 'messages')
    .filter((c) =>
      c.sender.name.toLowerCase().includes(q) ||
      c.lastMessage.toLowerCase().includes(q) ||
      c.messages.some((m) => m.text.toLowerCase().includes(q))
    );

  const matchRequests = conversations
    .filter((c) => (c.tabType || (c.isPaidRequest ? 'requests' : 'messages')) === 'requests')
    .filter((c) =>
      c.sender.name.toLowerCase().includes(q) ||
      c.lastMessage.toLowerCase().includes(q) ||
      c.messages.some((m) => m.text.toLowerCase().includes(q))
    );

  const matchSuggestions = Array.isArray(userSuggestions) ? userSuggestions : [];

  const hasResults = matchMessages.length > 0 || matchRequests.length > 0 || matchSuggestions.length > 0;

  const formatFollowers = (n) => {
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K Followers`;
    return `${n} Followers`;
  };

  const handleSuggestionMessage = (user) => {
    const existing = conversations.find((c) => c.sender.username === user.username);
    if (existing) {
      onSelectConversation(existing.id);
      onClearSearch();
    } else {
      onStartConversation(user);
    }
  };

  if (!hasResults) {
    return (
      <div className="msg-search msg-search--empty">
        <div className="msg-search__empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="msg-search__empty-icon">
            <circle cx="28" cy="28" r="18" stroke="currentColor" strokeWidth="2" opacity="0.3" />
            <path d="M42 42L56 56" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
          </svg>
          <h2 className="msg-search__empty-title">No results for "{query}"</h2>
          <p className="msg-search__empty-text">Try searching a different username or message content</p>
        </div>
      </div>
    );
  }

  return (
    <div className="msg-search">
      <div className="msg-search__content">
        {/* Messages Section */}
        {matchMessages.length > 0 && (
          <section className="msg-search__section">
            <h3 className="msg-search__section-label">Messages</h3>
            {matchMessages.map((conv) => (
              <button
                key={conv.id}
                type="button"
                className="msg-search__conv-row"
                onClick={() => { onSelectConversation(conv.id); onClearSearch(); }}
              >
                <img src={conv.sender.avatar} alt="" className="msg-search__conv-avatar" />
                <div className="msg-search__conv-info">
                  <span className="msg-search__conv-name">{highlightMatch(conv.sender.name, query)}</span>
                  <p className="msg-search__conv-preview">
                    {highlightMatch(
                      conv.messages[conv.messages.length - 1]?.text || conv.lastMessage,
                      query
                    )}
                  </p>
                </div>
              </button>
            ))}
          </section>
        )}

        {/* Requests Section */}
        {matchRequests.length > 0 && (
          <section className="msg-search__section">
            <h3 className="msg-search__section-label">Requests</h3>
            {matchRequests.map((conv) => (
              <button
                key={conv.id}
                type="button"
                className="msg-search__conv-row"
                onClick={() => { onSelectConversation(conv.id); onClearSearch(); }}
              >
                <img src={conv.sender.avatar} alt="" className="msg-search__conv-avatar" />
                <div className="msg-search__conv-info">
                  <span className="msg-search__conv-name">
                    {highlightMatch(conv.sender.name, query)}
                    <span className="msg-search__request-badge">REQUEST</span>
                  </span>
                  <p className="msg-search__conv-preview">
                    {highlightMatch(
                      conv.messages[conv.messages.length - 1]?.text || conv.lastMessage,
                      query
                    )}
                  </p>
                </div>
              </button>
            ))}
          </section>
        )}

        {/* Suggestions Section */}
        {matchSuggestions.length > 0 && (
          <section className="msg-search__section">
            <h3 className="msg-search__section-label">Suggestions</h3>
            <div className="msg-search__suggestions">
              {matchSuggestions.map((user) => (
                <div key={user.id} className="msg-search__suggestion-card">
                  <img src={user.avatar} alt="" className="msg-search__suggestion-avatar" />
                  <div className="msg-search__suggestion-info">
                    <span className="msg-search__suggestion-name">
                      {highlightMatch(user.username, query)}
                      {user.platform === 'twitch'
                        ? <TwitchIcon />
                        : user.platform === 'youtube'
                          ? <YouTubeIcon />
                          : null}
                    </span>
                    {typeof user.followers === 'number' && (
                      <span className="msg-search__suggestion-followers">{formatFollowers(user.followers)}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="msg-search__suggestion-btn"
                    onClick={() => handleSuggestionMessage(user)}
                  >
                    Send a Message
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

MessageSearchResults.propTypes = {
  query: PropTypes.string.isRequired,
  conversations: PropTypes.array.isRequired,
  userSuggestions: PropTypes.array,
  onSelectConversation: PropTypes.func.isRequired,
  onStartConversation: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
};

export default MessageSearchResults;
